/**
 * Mines math — standalone leaf module (no hono/drizzle) so the client can show the
 * multiplier ladder without pulling the server bundle. Board derivation + payouts are
 * pure and testable; the stateful round ledger lives in router.ts (D1).
 *
 * 5×5 grid, 4 mines. Every safe reveal multiplies the payout by the fair inverse of
 * the survival odds, shaved by the house edge. Cash out any time after one gem; hit a
 * mine and the round is dead. Stored on the leaderboard as ×100 (like crash).
 */

export const MINES_GRID = 25;
export const MINES_COUNT = 4;
export const MINES_EDGE = 0.01;
/** Leaderboard bounds, ×100. The full 21-gem clear caps at the crash ceiling. */
export const MINES_MIN = 100;
export const MINES_MAX = 100_000;

/** Max gems revealable (every non-mine tile). */
export const MINES_MAX_PICKS = MINES_GRID - MINES_COUNT;

/**
 * Multiplier after k safe reveals: (1 − edge) · Π_{i<k} (G−i)/(G−M−i), rounded to
 * 2dp. k=0 → 1.00 (nothing risked, nothing won). Strictly increasing in k.
 */
export function minesMultAfter(k: number): number {
  let m = 1 - MINES_EDGE;
  for (let i = 0; i < k; i++) {
    m *= (MINES_GRID - i) / (MINES_GRID - MINES_COUNT - i);
  }
  const rounded = Math.round(m * 100) / 100;
  return Math.min(Math.max(rounded, 1), MINES_MAX / 100);
}

/** Per-step survival chance going into reveal k+1 (k gems already showing). */
export function minesSurvival(k: number): number {
  return (MINES_GRID - MINES_COUNT - k) / (MINES_GRID - k);
}

/**
 * Derive the 4 mine positions from an HMAC digest (hex). Fisher-Yates over 0..24
 * driven by successive digest bytes; the slight modulo bias is irrelevant at toy
 * stakes. 64 hex chars = 32 bytes ≥ the 24 draws needed.
 */
export function deriveMines(digestHex: string): number[] {
  const tiles = Array.from({ length: MINES_GRID }, (_, i) => i);
  let b = 0;
  for (let i = MINES_GRID - 1; i > 0; i--) {
    const byte = Number.parseInt(digestHex.slice(b * 2, b * 2 + 2), 16);
    b = (b + 1) % 32;
    const j = byte % (i + 1);
    const tmp = tiles[i]!;
    tiles[i] = tiles[j]!;
    tiles[j] = tmp;
  }
  return tiles.slice(0, MINES_COUNT).sort((a, z) => a - z);
}

/** ×100 int for the scores table. */
export function minesToScoreValue(mult: number): number {
  return Math.min(Math.max(Math.round(mult * 100), MINES_MIN), MINES_MAX);
}
